import { Injectable, inject, signal, computed } from '@angular/core';
import { AuthState, User } from '../models/auth.models';
import { AuthRepository } from '../repositories/auth.repository';
import { LocalStorageService } from '../../../core/storage/local-storage.service';

@Injectable({
  providedIn: 'root'
})
export class AuthStateService {
  private authRepo = inject(AuthRepository);
  private localStorage = inject(LocalStorageService);
  private authCheckPromise: Promise<void> | null = null;

  // Core state holds the current authorization context
  private state = signal<AuthState>({
    status: 'initial',
    user: null,
    errorMessage: null,
    isLoading: false
  });

  // Public selectors using computed Signals
  readonly status = computed(() => this.state().status);
  readonly user = computed(() => this.state().user);
  readonly errorMessage = computed(() => this.state().errorMessage);
  readonly isLoading = computed(() => this.state().isLoading);
  readonly isAuthenticated = computed(() => this.state().status === 'authenticated');

  async checkAuth(): Promise<void> {
    if (this.authCheckPromise) {
      return this.authCheckPromise;
    }

    this.authCheckPromise = this.runAuthCheck().finally(() => {
      this.authCheckPromise = null;
    });

    return this.authCheckPromise;
  }

  private async runAuthCheck(): Promise<void> {
    this.state.set({ status: 'loading', user: null, errorMessage: null, isLoading: true });
    
    const rememberMe = this.localStorage.getBool('remember_me') ?? false;
    if (!rememberMe) {
      this.authRepo.clearTokens();
      this.state.set({ status: 'unauthenticated', user: null, errorMessage: null, isLoading: false });
      return;
    }

    const userId = this.localStorage.getUserId();
    if (!userId) {
      this.state.set({ status: 'unauthenticated', user: null, errorMessage: null, isLoading: false });
      return;
    }

    try {
      const user = await this.authRepo.getCurrentUser(userId);
      this.state.set({ status: 'authenticated', user, errorMessage: null, isLoading: false });
    } catch (error) {
      const refreshed = await this.tryRefreshToken();
      if (refreshed) {
        try {
          const retryUserId = this.localStorage.getUserId();
          if (retryUserId) {
            const user = await this.authRepo.getCurrentUser(retryUserId);
            this.state.set({ status: 'authenticated', user, errorMessage: null, isLoading: false });
            return;
          }
        } catch {
          // Fall through to clear auth data below.
        }
      }

      this.authRepo.clearTokens();
      this.state.set({ status: 'unauthenticated', user: null, errorMessage: null, isLoading: false });
    }
  }

  private async tryRefreshToken(): Promise<boolean> {
    const refreshToken = this.authRepo.getRefreshToken();
    if (!refreshToken) return false;

    try {
      const response = await this.authRepo.refreshToken(refreshToken);
      if (!response.accessToken || !response.refreshToken) {
        return false;
      }

      this.authRepo.saveTokens(response.accessToken, response.refreshToken, response.userId);
      return true;
    } catch {
      this.authRepo.clearTokens();
      return false;
    }
  }

  async ensureAuthResolved(): Promise<void> {
    const status = this.status();
    if (status === 'initial' || status === 'loading') {
      await this.checkAuth();
    }
  }

  async login(email: string, password: string, rememberMe: boolean): Promise<void> {
    this.state.set({ status: 'loading', user: null, errorMessage: null, isLoading: true });
    try {
      const response = await this.authRepo.login(email, password);
      
      if (response.requiresVerification) {
        this.state.set({
          status: 'needsVerification',
          user: null,
          errorMessage: response.email || email,
          isLoading: false
        });
        return;
      }

      if (response.accessToken && response.refreshToken) {
        this.authRepo.saveTokens(response.accessToken, response.refreshToken, response.userId);
        this.localStorage.setBool('remember_me', rememberMe);
      }

      const user: User = {
        id: response.userId,
        displayName: response.displayName,
        email: response.email,
        isPremium: false,
        createdAt: response.expiresAt
      };

      this.state.set({ status: 'authenticated', user, errorMessage: null, isLoading: false });
    } catch (error: any) {
      this.state.set({ status: 'error', user: null, errorMessage: error.message || 'Login failed', isLoading: false });
    }
  }

  async register(email: string, password: string, displayName: string): Promise<void> {
    this.state.set({ status: 'loading', user: null, errorMessage: null, isLoading: true });
    try {
      const response = await this.authRepo.register(email, password, displayName);

      if (response.requiresVerification) {
        this.state.set({
          status: 'needsVerification',
          user: null,
          errorMessage: response.email || email,
          isLoading: false
        });
        return;
      }

      if (response.accessToken && response.refreshToken) {
        this.authRepo.saveTokens(response.accessToken, response.refreshToken, response.userId);
        this.localStorage.setBool('remember_me', true);
      }

      const user: User = {
        id: response.userId,
        displayName: response.displayName,
        email: response.email,
        isPremium: false,
        createdAt: response.expiresAt
      };

      this.state.set({ status: 'authenticated', user, errorMessage: null, isLoading: false });
    } catch (error: any) {
      this.state.set({ status: 'error', user: null, errorMessage: error.message || 'Registration failed', isLoading: false });
    }
  }

  async verifyEmail(email: string, code: string): Promise<void> {
    this.state.set({ status: 'loading', user: this.state().user, errorMessage: null, isLoading: true });
    try {
      const response = await this.authRepo.verifyEmail(email, code);

      if (response.accessToken && response.refreshToken) {
        this.authRepo.saveTokens(response.accessToken, response.refreshToken, response.userId);
      }

      const user: User = {
        id: response.userId,
        displayName: response.displayName,
        email: response.email,
        isPremium: false,
        createdAt: response.expiresAt
      };

      this.state.set({ status: 'authenticated', user, errorMessage: null, isLoading: false });
    } catch (error: any) {
      this.state.set({ status: 'error', user: null, errorMessage: error.message || 'Verification failed', isLoading: false });
    }
  }

  async resendOtp(email: string): Promise<void> {
    try {
      await this.authRepo.resendOtp(email);
    } catch (error: any) {
      this.state.update((curr) => ({
        ...curr,
        status: 'error',
        errorMessage: error.message || 'Failed to resend verification code'
      }));
    }
  }

  async requestPasswordReset(email: string): Promise<void> {
    this.state.set({ status: 'loading', user: null, errorMessage: null, isLoading: true });
    try {
      await this.authRepo.requestPasswordReset(email);
      this.state.set({ status: 'unauthenticated', user: null, errorMessage: null, isLoading: false });
    } catch (error: any) {
      this.state.set({ status: 'error', user: null, errorMessage: error?.message || 'Failed to request password reset', isLoading: false });
      throw error;
    }
  }

  async resetPassword(email: string, code: string, newPassword: string): Promise<void> {
    this.state.set({ status: 'loading', user: null, errorMessage: null, isLoading: true });
    try {
      await this.authRepo.resetPassword(email, code, newPassword);
      this.state.set({ status: 'unauthenticated', user: null, errorMessage: null, isLoading: false });
    } catch (error: any) {
      this.state.set({ status: 'error', user: null, errorMessage: error?.message || 'Failed to reset password', isLoading: false });
      throw error;
    }
  }

  async logout(): Promise<void> {
    try {
      await this.authRepo.logout();
    } catch (error) {
      console.error('Logout API call failed; clearing local session', error);
    }
    this.authRepo.clearTokens();
    this.localStorage.remove('remember_me');
    this.state.set({ status: 'unauthenticated', user: null, errorMessage: null, isLoading: false });
  }

  async deleteAccount(): Promise<void> {
    const user = this.user();
    if (!user) return;

    await this.authRepo.deleteAccount(user.id);
    this.authRepo.clearTokens();
    this.localStorage.remove('remember_me');
    this.state.set({ status: 'unauthenticated', user: null, errorMessage: null, isLoading: false });
  }

  clearError(): void {
    this.state.update((curr) => ({ ...curr, errorMessage: null, status: curr.status === 'error' ? 'unauthenticated' : curr.status }));
  }
}
